import { computed, ref } from "vue";
import { defineStore } from "pinia";
import {
  SearchResponseState,
  stateError,
  stateInitial,
  stateLoaded,
  stateLoading,
} from "@shared/models/state";
import { clientId } from "../constants";
import { TwinReporting, TwinReportingTypes } from "@inctec/smx-ui-client";

type MaintenanceReportGroupsQuery = {
  clientId: string;
  page: number;
  pageSize: number;
};

const defaultQuery: MaintenanceReportGroupsQuery = {
  clientId,
  page: 1,
  pageSize: 100,
};

export const useMaintenanceReportGroupsStore = defineStore(
  "maintenanceReportGroups",
  () => {
    const reportGroupsLoadingState = ref<
      SearchResponseState<
        TwinReportingTypes.ReportGroup[],
        MaintenanceReportGroupsQuery
      >
    >({
      ...stateInitial,
      result: [],
      query: defaultQuery,
      page: defaultQuery.page,
      pageSize: defaultQuery.pageSize,
      pagesTotal: 0,
      total: 0,
    });

    async function loadMaintenanceReportGroups(keepCache = true) {
      if (
        keepCache &&
        (reportGroupsLoadingState.value.loading ||
          reportGroupsLoadingState.value.loaded)
      ) {
        return reportGroupsLoadingState.value.result;
      }

      reportGroupsLoadingState.value = {
        ...reportGroupsLoadingState.value,
        ...stateLoading,
      };

      const response = await TwinReporting.searchReportGroups({
        body: defaultQuery,
      }).catch((e) => {
        reportGroupsLoadingState.value = {
          ...reportGroupsLoadingState.value,
          ...stateError,
        };
        throw e;
      });

      reportGroupsLoadingState.value = {
        ...stateLoaded,
        query: defaultQuery,
        ...response.data,
      };

      return reportGroupsLoadingState.value.result;
    }

    async function refreshMaintenanceReportGroups() {
      return loadMaintenanceReportGroups(false);
    }

    const reportGroups = computed(
      () => reportGroupsLoadingState.value.result ?? [],
    );

    function setReportGroups(newGroups: TwinReportingTypes.ReportGroup[]) {
      reportGroupsLoadingState.value = {
        ...reportGroupsLoadingState.value,
        ...stateLoaded,
        result: newGroups,
      };
    }

    return {
      reportGroupsLoadingState,
      reportGroups,
      loadMaintenanceReportGroups,
      refreshMaintenanceReportGroups,
      setReportGroups,
    };
  },
);
